import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import { FaAmazon } from 'react-icons/fa';
import { FiX } from 'react-icons/fi';

import OptimizedImage from './OptimizedImage';

interface StoreLink {
  name: string;
  url: string;
  icon?: React.ComponentType<{ className?: string }>;
  iconText?: string;
}

const storeLinks: StoreLink[] = [
  {
    name: 'Amazon',
    url: 'https://a.co/d/drPmmD8',
    icon: FaAmazon,
  },
  {
    name: 'Barnes & Noble',
    url: 'https://www.barnesandnoble.com/w/konnect-better-mo-lidsky-phd/1148503636',
    iconText: 'B&N',
  },
  {
    name: 'Indigo',
    url: 'https://www.indigo.ca/products/konnect-better-unlocking-the-power-of-your-relationships-volume-one-committed-connections?variant=46646021193938',
    iconText: 'indigo',
  },
  {
    name: 'Walmart',
    url: 'https://www.walmart.com/ip/Konnect-Better-Unlocking-the-Power-of-Your-Relationships-Volume-One-Committed-Connections-Hardcover-9798891385795/18134763639',
    iconText: 'W',
  },
];

// Animation variants for staggered content
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
};

const POPUP_SESSION_KEY = 'konnect_popup_shown';

const PreOrderPopup = () => {
  const [isOpen, setIsOpen] = useState(false);

  const handleDismiss = useCallback(() => {
    setIsOpen(false);
    sessionStorage.setItem(POPUP_SESSION_KEY, 'true');
  }, []);

  useEffect(() => {
    // Check if popup was already shown this session
    const alreadyShown = sessionStorage.getItem(POPUP_SESSION_KEY);
    if (alreadyShown) return;

    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  // Keyboard navigation - Escape to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleDismiss();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleDismiss]);

  const handlePreOrder = () => {
    window.open(storeLinks[0].url, '_blank');
    handleDismiss();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleDismiss();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/70"
          onClick={handleBackdropClick}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3, type: 'spring', damping: 25 }}
            className="relative w-full max-w-[340px] sm:max-w-md md:max-w-lg bg-card dark:bg-panel rounded-2xl shadow-2xl overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="popup-title"
          >
            {/* Close Button */}
            <button
              onClick={handleDismiss}
              className="absolute top-3 right-3 z-10 p-2.5 rounded-full bg-card/90 dark:bg-dark-card/90 hover:bg-card dark:hover:bg-dark-card shadow-md transition-all duration-200 hover:scale-110"
              aria-label="Close popup"
            >
              <FiX className="w-5 h-5 text-theme-primary" />
            </button>

            <div className="flex flex-col">
              {/* Book Image */}
              <div className="w-full bg-gradient-to-br from-brand-primary/20 to-brand-secondary/20 p-3 sm:p-5 flex items-center justify-center relative">
                <div className="w-48 sm:w-64 md:w-80 bg-card dark:bg-panel rounded-2xl shadow-2xl p-2 aspect-[3/2] overflow-hidden relative">
                  <div className="rounded-xl h-full overflow-hidden">
                    <OptimizedImage
                      src="/images/konnect_book_horizontal.png"
                      alt="Konnect Better Book"
                      priority={true}
                    />
                  </div>
                </div>
              </div>

              {/* Content with staggered animations */}
              <motion.div
                className="w-full px-4 sm:px-6 lg:px-8 py-3 sm:py-4 text-center"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {/* Badge with pulse animation */}
                <motion.div variants={itemVariants} className="inline-block">
                  <motion.span
                    className="inline-block bg-brand-secondary/20 text-brand-secondary px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold mb-1.5 sm:mb-2"
                    animate={{ scale: [1, 1.02, 1] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: 'reverse',
                    }}
                  >
                    Available Now
                  </motion.span>
                </motion.div>

                <motion.h2
                  id="popup-title"
                  variants={itemVariants}
                  className="text-sm sm:text-base md:text-lg font-bold text-theme-primary mb-1.5 sm:mb-2 leading-tight px-2"
                >
                  konnect better: unlocking the power of your relationships
                </motion.h2>

                <motion.p
                  variants={itemVariants}
                  className="text-theme-secondary mb-2.5 sm:mb-3 text-[11px] sm:text-xs md:text-sm leading-snug px-2"
                >
                  Discover practical insights and real tools to strengthen your
                  connections.
                </motion.p>

                {/* Text-based Achievement Display */}
                <motion.div
                  variants={itemVariants}
                  className="mb-2.5 sm:mb-3 space-y-1"
                >
                  <a
                    href="https://www.bookmarketingprofits.com/PinnacleAwardsFall2025.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-[10px] sm:text-xs font-semibold text-brand-secondary hover:text-brand-primary transition-colors duration-200"
                  >
                    Pinnacle Book Achievement Award Winner
                  </a>
                  <a
                    href="https://www.amazon.com/Konnect-Better-Unlocking-Relationships-Connections/dp/B0FV14D5V1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-[10px] sm:text-xs font-semibold text-brand-secondary hover:text-brand-primary transition-colors duration-200"
                  >
                    Amazon Best Seller
                  </a>
                </motion.div>

                {/* Primary CTA with pulse effect */}
                <motion.div variants={itemVariants}>
                  <motion.button
                    onClick={handlePreOrder}
                    className="w-full btn-secondary py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm font-semibold mb-2.5 sm:mb-3"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Order Now
                  </motion.button>
                </motion.div>

                {/* Store Icons - Larger touch targets */}
                <motion.div variants={itemVariants}>
                  <p className="text-[10px] sm:text-xs text-theme-secondary mb-1.5 sm:mb-2">
                    Also available at:
                  </p>
                  <div className="flex justify-center gap-1.5 sm:gap-2.5">
                    {storeLinks.map((store, index) => (
                      <motion.a
                        key={store.name}
                        href={store.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 md:w-11 md:h-11 rounded-full bg-card dark:bg-panel border-2 border-theme hover:border-brand-secondary hover:bg-brand-secondary/10 transition-all duration-200"
                        whileHover={{ scale: 1.15, rotate: 5 }}
                        whileTap={{ scale: 0.95 }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                        title={store.name}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {store.icon ? (
                          <store.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 text-theme-primary" />
                        ) : (
                          <span className="text-[8px] sm:text-[9px] md:text-[10px] font-bold text-theme-primary">
                            {store.iconText}
                          </span>
                        )}
                      </motion.a>
                    ))}
                  </div>
                </motion.div>

                {/* Dismiss Link - More touch-friendly */}
                <motion.button
                  variants={itemVariants}
                  onClick={handleDismiss}
                  className="mt-2 sm:mt-3 py-1.5 text-[10px] sm:text-xs md:text-sm text-theme-secondary hover:text-primary dark:hover:text-theme-primary transition-colors underline-offset-2 hover:underline"
                >
                  No thanks, maybe later
                </motion.button>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PreOrderPopup;
